import React, { useCallback } from 'react';
import { StanDataRowProps } from './StanDataRow.types';
import styles from './StanDataRow.module.css';
import { cn } from '@/lib/utils/cn';
import { calculateActionsWidth, handleRowClick } from './StanDataRow.logic';
import { useStanDataRowRenderer } from './useStanDataRowRenderer';

export const StanDataRow = React.memo<StanDataRowProps>(
    ({
        columns = [],
        actionButtons = [],
        gridTemplate,
        className,
        variant = 'default',
        clickable = false,
        onClick,
        maxActionButtonCount,
    }) => {
        const handleClick = useCallback(
            (e: React.MouseEvent) => {
                handleRowClick(e, onClick);
            },
            [onClick]
        );

        // Use provided gridTemplate or generate equal distribution
        const finalGridTemplate = gridTemplate || `repeat(${columns.length}, 1fr)`;

        // Use the renderer hook to separate logic from view (MVC pattern)
        const { renderedColumns, renderedActionButtons } = useStanDataRowRenderer({
            columns,
            actionButtons: actionButtons,
        });

        if (columns.length === 0) return null;

        return (
            <div
                className={cn(
                    styles['stan-data-row'],
                    styles[`variant-${variant}`],
                    clickable && styles.clickable,
                    className
                )}
                onClick={handleClick}
            >
                <div
                    style={{
                        gridTemplateColumns: finalGridTemplate,
                    }}
                    className={styles['data-row-content']}
                >
                    {renderedColumns}
                </div>

                {/* Action buttons container */}
                <div
                    className={styles['actions-container']}
                    style={{
                        width: maxActionButtonCount ? `${calculateActionsWidth(maxActionButtonCount)}px` : 'auto',
                    }}
                >
                    {renderedActionButtons}
                </div>
            </div>
        );
    }
);

StanDataRow.displayName = 'StanDataRow';
